import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { UserInterface } from '../../../../interfaces/User';
import { RootState } from '../../../../store';
import { useOrariNegozio } from '../../hooks/useOrariNegozio';
import { usePresenzeGiorni } from '../../hooks/usePresenzeGiorni';
import type { PresenzeModo } from '../../utility_objects/preferenzeCalendario';
import { CellaPresenze } from './CellaPresenze';

// Il calendario del giornaliero studio legge gli orari senza operatori selezionati.
const NESSUN_UTENTE: UserInterface[] = [];

interface FasciaPresenzeGiornoProps {
  pointOfSaleId: string;
  /**
   * Data selezionata nella pagina, così com'è nel suo store: gli orari del negozio
   * si leggono con la stessa chiave del calendario (useOrariNegozio).
   */
  selectedDate: Date;
  /**
   * Modalità della fascia, la riga Presenze del riquadro Visualizzazione del
   * giornaliero Studio (scelta del calendario, nella sua slice).
   */
  modo: PresenzeModo;
}

/**
 * Fascia Presenze di un solo giorno, fra la barra delle viste e il calendario del
 * giornaliero studio (viste Verticale e Orizzontale): una cella a tutta larghezza,
 * fuori da Syncfusion e senza colonne a cui allinearsi, come l'Agenda dell'agenda
 * negozio, con la data corta in testa. Stessa cella, stesso popover e stessi dati
 * (usePresenzeGiorni con le stesse chiavi: la cache è condivisa con l'agenda
 * negozio); gli orari del negozio sono quelli del calendario della pagina (vista
 * Giorno, nessun operatore). Una chip per persona, col solo username in «Presenze»
 * e col testo completo in «Presenze totali»; alta almeno 48px e quanto serve (il
 * calendario sotto si accorcia: useSchedulerLayoutRefresh). Va montata solo con una
 * modalità scelta e un punto vendita; finché i filtri non sono caricati la cella
 * resta in caricamento.
 */
export const FasciaPresenzeGiorno = memo(function FasciaPresenzeGiorno({
  pointOfSaleId,
  selectedDate,
  modo,
}: FasciaPresenzeGiornoProps): JSX.Element {
  const { filtersLoad } = useSelector(
    (state: RootState) => state.bluBookingCalendarFilters
  );
  // La data selezionata può arrivare anche come stringa: conta solo il giorno.
  const giorno = dayjs(selectedDate).format('YYYY-MM-DD');
  const giorni = useMemo(() => [giorno], [giorno]);
  const attiva = modo !== 'nessuna' && pointOfSaleId !== '' && !!filtersLoad;
  const presenze = usePresenzeGiorni({ pointOfSaleId, giorni, attiva });
  const testo = modo === 'totali' ? 'completo' : 'breve';
  const orariNegozio = useOrariNegozio({
    pointOfSaleId,
    selectedDate,
    view: 'Day',
    selectedUsers: NESSUN_UTENTE,
    attiva: attiva && testo === 'completo',
  });

  return (
    <Box
      role="group"
      aria-label="Presenze"
      sx={{
        // Alta quanto le chip, senza tetto.
        minHeight: 48,
        flexShrink: 0,
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr)',
        overflow: 'hidden',
        backgroundColor: 'var(--base-foreground-color)',
      }}
    >
      {presenze.tuttoInErrore ? (
        // Piccola e centrata, come nell'agenda negozio.
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          sx={{ alignSelf: 'center', textAlign: 'center', px: 1 }}
        >
          Presenze non disponibili
        </Typography>
      ) : (
        <CellaPresenze
          giorno={presenze.giorni[0]}
          statoAssenze={presenze.statoAssenze}
          testo={testo}
          orarioNegozio={orariNegozio?.[giorno] ?? null}
          conData
          bordo={false}
        />
      )}
    </Box>
  );
});
